<style scoped>
#editor {
  display: flex;
  align-items: top;
  justify-content: center;
  flex-direction: column;
  flex-wrap: wrap;
  font-family: 'Lato', sans-serif;
  color: #1a1a40;
}

.player {
  padding-top: 56.25%;
  position: relative;
  margin: 10px 0;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}



iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.brand-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a1a40;
  margin-bottom: 1rem;
  text-align: center;
}

/* Custom UI Elements */
.color-picker-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.color-input {
  width: 30px;
  height: 30px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
  background: none;
}

.color-label {
  font-size: 0.875rem;
  color: #4b5563;
  text-transform: capitalize;
  cursor: pointer;
}

.btn-action {
  background-color: #1a1a40 !important;
  color: white !important;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.btn-secondary {
  border: 1px solid #1a1a40 !important;
  color: #1a1a40 !important;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.editor-content {
  width: 800px;
  max-width: 100%;
  margin: 0 auto;
  position: relative;
}

.close-btn {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
}
</style>

<template>
  <div id="editor">
    <!-- control -->
    <div class="editor-content">
      <v-btn icon class="close-btn" @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>

      <div class="brand-title">
        {{ getTitle() }}
      </div>

      <!-- video -->
      <div class="player">
        <!-- <youtube :video-id="videoId" v-if="videoId != null" /> -->
        <iframe
          width="560"
          height="315"
          :src="videoUrl(videoId, videoTime)"
          title="YouTube video player"
          frameborder="0"
          allow="
            accelerometer;
            autoplay;
            clipboard-write;
            encrypted-media;
            gyroscope;
            picture-in-picture;
            web-share;
          "
          allowfullscreen
        ></iframe>
      </div>

      <!-- paper -->
      <div id="paper" :style="paperSize">
        <component
          :is="currentComponent"
          :roundel="roundel"
          :number="number"
          @openColorInput="openColorInput"
          :roundelScale="roundelScale / 100"
        ></component>
      </div>

      <!-- number field -->
      <div v-if="numberEditable" class="mt-4">
        <v-text-field
          outlined
          dense
          color="#c5a059"
          :label="design.version === 'v2' ? 'Tail Number' : 'Tail Number (2 digits)'"
          placeholder="00"
          v-model="number"
          type="number"
          :min="minNumber"
          :max="maxNumber"
          :rules="[
            (n) => isNumberValid(n) || (design.version === 'v2' ? `Number must be between ${minNumber} and ${maxNumber}` : 'Number must be 2 digits')
          ]"
        ></v-text-field>
      </div>

      <div style="display: flex; height: 300px; margin-top: 1rem; gap: 1rem;">
        <!-- color palette -->
        <div
          v-if="currentComponent"
          style="
            display: flex;
            flex-direction: column;
            flex: 1 1;
            height: 100%;
            overflow-y: auto;
            border: 1px solid #e5e7eb;
            border-radius: 4px;
            padding: 1rem;
          "
        >
          <div class="font-weight-bold mb-3" style="color: #1a1a40;">Colors</div>
          <div v-for="[k, v] of Object.entries(colors)" :key="k" class="color-picker-item" style="display:flex; align-items:center; margin-bottom:0.5rem;">
            <v-menu
              v-model="colorMenus[k]"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-bind="attrs"
                  v-on="on"
                  :style="{
                    width: '36px',
                    height: '36px',
                    backgroundColor: v,
                    borderRadius: '50%',
                    border: '2px solid #ddd',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    flexShrink: 0
                  }"
                  :ref="k"
                ></div>
              </template>
              <div class="d-flex flex-column white rounded">
                <div class="d-flex justify-end pa-2" v-if="hasEyeDropper">
                  <v-btn
                    icon
                    small
                    color="grey darken-2"
                    @click="pickColor(k)"
                    title="Pick Color from Screen"
                  >
                    <v-icon>mdi-eyedropper</v-icon>
                  </v-btn>
                </div>
                <v-color-picker
                  v-touch-support
                  :value="v"
                  @input="updateColor(k, $event)"
                  mode="hexa"
                  flat
                ></v-color-picker>
              </div>
            </v-menu>
            <div style="margin-left:12px; font-weight:500; text-transform:capitalize; color:#333; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0;">
                {{ k }}
            </div>
          </div>
        </div>
        <!-- roundel picker -->
        <roundel-picker
          v-if="roundelEditable"
          style="flex: 1 1; border: 1px solid #e5e7eb; border-radius: 4px; padding: 1rem;"
          v-model="roundel"
          :roundelScale="roundelScale"
          @changeRoundelScale="changeRoundelScale"
        />
      </div>

      <!-- print -->
      <div id="printable" v-if="printing" style="position: absolute; left: 0; top: 0; width: 100%; z-index: -1000; opacity: 0;">
        <div class="print-page" data-copy-index="1">
          <component
            :is="currentComponent"
            :roundel="roundel"
            :number="number"
            :qrVisible="true"
            :roundelScale="roundelScale / 100"
          ></component>
        </div>
      </div>
      <div
        style="
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          margin-top: 1.5rem;
          gap: 1rem;
        "
      >
        <v-text-field
          style="flex: 1; min-width: 140px;"
          v-model="numCopies"
          hide-details
          outlined
          dense
          color="#c5a059"
          label="Copies"
          type="number"
          min="1"
        />
        <v-btn class="btn-action" height="40" v-on:click="printOut()"
          >Print</v-btn
        >
        <v-btn class="btn-action" height="40" v-on:click="checkPdfPassword()" :loading="downloading" v-if="!isIOS"
          >Download</v-btn
        >
        <v-btn class="btn-secondary" height="40" text v-on:click="reset()">Reset</v-btn>
      </div>

      <!-- Password Dialog -->
      <v-dialog v-model="pdfPasswordDialog" max-width="400">
        <v-card>
          <v-card-title class="headline">PDF Password</v-card-title>
          <v-card-text>
            The password for the PDF is <strong>origami-skin</strong>.
            <v-checkbox
              v-model="dontShowPdfPassword"
              label="Don't show this again"
              hide-details
              class="mt-4"
            ></v-checkbox>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="pdfPasswordDialog = false">
              Cancel
            </v-btn>
            <v-btn color="primary" text @click="confirmDownloadPdf">
              Download
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
// import printJS from "print-js";
import { createFighterComponent } from "@/fighterComponent.js";
import { createFighterComponentV2 } from "@/fighterComponentV2.js";
import RoundelPicker from "./RoundelPicker.vue";
import { logEvent } from "firebase/analytics";
import { getIdFromURL, getTimeFromURL } from "vue-youtube-embed";
import { PrintManager } from "@/utils/printManager.js";
import {
  findFighterDesign,
  getLastFighterDesign,
} from "@/data/fighterDesigns.js";
import version from "@/data/version.js";
import semver from "semver";

export default {
  props: ["analytics", "fighter"],
  components: { RoundelPicker },
  data() {
    return {
      paperSize: "width:100%;max-width:800px;",

      videoId: null,
      videoTime: null,
      design: null,
      currentComponent: null,
      colors: {}, // Initialize as empty object
      saveEnabled: false,
      printing: false,
      downloading: false,
      numCopies: 1,
      numberEditable: false,
      roundelEditable: true,
      number: null,
      minNumber: null,
      maxNumber: null,
      roundel: null,
      roundelScale: 100,
      
      pdfPasswordDialog: false,
      dontShowPdfPassword: false,

      colorMenus: {},
      hasEyeDropper: false,
      isIOS: false,
    };
  },
  created() {
    this.hasEyeDropper = 'EyeDropper' in window;
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  },
  async mounted() {
    console.log("Editor mounted");
    console.log(`Fighter required: ${this.fighter}`);
    this.onChangeFighter(this.fighter);
    console.log(`Fighter design: ${this.design.name}`);
  },
  methods: {
    changeRoundelScale(scale) {
      this.roundelScale = scale;
    },
    getTitle() {
      if (this.design) {
        return this.design.name;
      } else {
        ("");
      }
    },
    onChangeFighter(fighter) {
      this.design = this.getDesign(fighter);
    },
    async reset() {
      this.numCopies = 1;
      localStorage.setItem(this.design.fileName, null);
      await this.load(this.design);
    },
    setVideo(videoId, videoTime) {
      this.videoId = videoId;
      this.videoTime = videoTime;
      console.log(this.videoUrl(this.videoId, this.videoTime));
    },
    videoUrl(videoId, videoTime) {
      return `https://www.youtube.com/embed/${videoId}?t=${videoTime}`;
    },
    getDesign(fighter) {
      return findFighterDesign(fighter) ?? getLastFighterDesign();
    },
    isNumberValid(number) {
      if (this.design.version === "v2") {
        const n = parseInt(number);
        const min = this.design.minNumber ?? 0;
        const max = this.design.maxNumber ?? 9;
        return !isNaN(n) && n >= min && n <= max;
      } else {
        const regex = /^\d{2}$/;
        return regex.test(number);
      }
    },
    enableSave() {
      this.saveEnabled = true;
    },
    disableSave() {
      this.saveEnabled = false;
    },
    async replace(design, colors, roundel, number) {
      console.log("replace");

      console.log("\tcreate fighter component");
      
      let result;
      if (design.version === "v2") {
        result = await createFighterComponentV2(design.fileName);
      } else {
        result = await createFighterComponent(design.fileName);
      }

      for (let key in result.colors) {
        console.log(`[result colors] ${key}`);
      }

      // read colors from template
      this.colors = result.colors;
      
      // Normalize to 8-digit HEXA to ensure Alpha slider appears
      for (const key in this.colors) {
          let c = this.colors[key];
          if (c && c.startsWith('#') && c.length === 7) {
              this.colors[key] = c + 'FF';
          }
      }

      if (colors) {
        for (const key in colors) {
          if (this.colors[key] !== undefined) {
             let c = colors[key];
             // Also normalize loaded overrides
             if (c && c.startsWith('#') && c.length === 7) {
                 c += 'FF';
             }
             this.colors[key] = c;
          }
        }
      }

      console.log("\tupdate roundel on replace");
      this.roundelEditable =
        design.roundelEditable !== undefined ? design.roundelEditable : true;
      if (roundel) {
        this.roundel = roundel;
      } else {
        this.roundel = design.roundel;
      }

      this.setVideo(
        getIdFromURL(design.videoUrl),
        getTimeFromURL(design.videoUrl),
      );

      console.log("\tupdate number on replace");
      // Populate min/max if present (V2)
      this.minNumber = design.minNumber;
      this.maxNumber = design.maxNumber;
      
      if (design.number === undefined) {
        this.numberEditable = false;
      } else {
        this.numberEditable =
          design.numberEditable !== undefined ? design.numberEditable : true;
      }

      if (number) {
        this.number = number;
      } else {
        this.number = design.number || (design.version === "v2" ? design.minNumber : "00");
      }

      console.log("\tassign fighter component");
      this.currentComponent = result.component;
    },
    save() {
      if (!this.saveEnabled) return;

      console.log("save");

      const editorState = {
        fileName: this.design.fileName,
        roundel: this.roundel,
        colors: this.colors,
        number: this.number,
      };
      localStorage.setItem(this.design.fileName, JSON.stringify(editorState));
      localStorage.setItem("appVersion", version);
      console.log(`Save ${this.design.fileName} with appVersion ${version}`);
    },
    async load(design) {
      console.log("load");
      this.disableSave();

      let editorState = null;
      const lastVersion = localStorage.getItem("appVersion");
      console.log(`Last Version: ${lastVersion}, Current Version: ${version}`);
      if (
        lastVersion &&
        semver.eq(semver.coerce(lastVersion), semver.coerce(version))
      ) {
        const s = localStorage.getItem(design.fileName);
        if (s) {
          try {
            editorState = JSON.parse(s);
          } catch (e) {
            console.log(e);
          }
        }
      }

      if (editorState) {
        await this.replace(
          design,
          editorState.colors,
          editorState.roundel,
          editorState.number,
        );
        console.log(`Load ${editorState.fileName}`);
      } else {
        await this.replace(design);
      }

      this.enableSave();
    },
    // New update method for v-color-picker
    updateColor(prop, val) {
      console.log(`update prop ${prop} to`, val);
      // use .hexa to include alpha channel
      const colorVal = (typeof val === 'object' && val.hexa) ? val.hexa : val;
      
      // Track customization if value changed
      if (this.colors[prop] !== colorVal) {
        logEvent(this.analytics, "design_customize", {
          design_name: this.design.name,
          color_part: prop,
          new_color: colorVal
        });
      }

      this.$set(this.colors, prop, colorVal);
      this.save();
    },
    onSelectColor(prop, event) {
      this.updateColor(prop, event.target.value);
    },
    openColorInput(target) {
      console.log(`open color input of ${target}`);
      if (this.colorMenus && this.colorMenus[target] !== undefined) {
           this.$set(this.colorMenus, target, true);
      } else {
         // Fallback for V1 legacy
         let colorInput = this.$refs[target][0];
         if (colorInput) {
             colorInput.focus();
             colorInput.click();
         }
      }
    },
    async pickColor(prop) {
      if (!window.EyeDropper) return;

      const eyeDropper = new window.EyeDropper();

      try {
        const result = await eyeDropper.open();
        // result.sRGBHex returns the color in #RRGGBB format
        this.updateColor(prop, result.sRGBHex);
        this.$set(this.colorMenus, prop, false); // Close menu after picking
      } catch (e) {
        console.log("EyeDropper closed or failed", e);
      }
    },
    printOut() {
      // Refactored analytics event
      logEvent(this.analytics, "design_print", {
        design_name: this.design.name,
        copies: this.numCopies
      });

      this.printing = true;
      this.$nextTick(() => {
        const printable = document.getElementById('printable');
        PrintManager.print(printable, this.numCopies);
        this.printing = false;
      });
    },
    checkPdfPassword() {
        const hideDialog = localStorage.getItem('hidePdfPasswordDialog');
        if (hideDialog === 'true') {
            this.downloadPdf();
        } else {
            this.pdfPasswordDialog = true;
        }
    },
    confirmDownloadPdf() {
        if (this.dontShowPdfPassword) {
            localStorage.setItem('hidePdfPasswordDialog', 'true');
        }
        this.pdfPasswordDialog = false;
        this.downloadPdf();
    },
    downloadPdf() {
      // Refactored analytics event
      logEvent(this.analytics, "design_download", {
        design_name: this.design.name,
        file_type: "pdf"
      });

      this.downloading = true;
      this.printing = true;
      this.$nextTick(async () => {
        // Wait for potential asset loading (roundels, fonts)
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const printable = document.getElementById('printable');
        try {
            await PrintManager.downloadPdf(printable, `${this.design.name}_PW_origami-skin.pdf`, 'origami-skin');
        } catch (e) {
            console.error(e);
        }
        this.printing = false;
        this.downloading = false;
      });
    },

  },
  watch: {
    fighter: {
      handler(value) {
        this.onChangeFighter(value);
      },
    },
    design: {
      handler(value) {
        this.load(value);
      },
    },
    roundel: {
      handler() {
        this.save();
      },
    },
    number: {
      handler() {
        this.save();
      },
    },
  },
  directives: {
    'touch-support': {
      inserted(el) {
        // Helper to trigger mouse events
        const dispatchMouseEvent = (type, touch, target) => {
          const event = new MouseEvent(type, {
            bubbles: true,
            cancelable: true,
            view: window,
            clientX: touch.clientX,
            clientY: touch.clientY,
            screenX: touch.screenX,
            screenY: touch.screenY
          });
          target.dispatchEvent(event);
        };

        // Targets to apply touch support to: Canvas and Sliders
        // Use a small timeout to ensure child components are rendered if needed, 
        // though inserted hook usually implies el is in DOM. 
        // Vuetify renders these immediately usually.
        const applyTouch = () => {
            const targets = el.querySelectorAll('.v-color-picker__canvas, .v-color-picker__sliders');
            
            targets.forEach(target => {
                // Prevent duplicate listeners if this runs multiple times
                if (target._touchAttached) return;
                target._touchAttached = true;

                // Touch Start -> Mouse Down
                target.addEventListener('touchstart', (e) => {
                  if (e.cancelable) e.preventDefault(); // Stop scrolling
                  const touch = e.touches[0];
                  dispatchMouseEvent('mousedown', touch, target);
                }, { passive: false });

                // Touch Move -> Mouse Move (on Window)
                // We listen on target but dispatch to window because Vuetify's logic 
                // attaches mousemove to window after mousedown.
                target.addEventListener('touchmove', (e) => {
                  if (e.cancelable) e.preventDefault();
                  const touch = e.touches[0];
                  dispatchMouseEvent('mousemove', touch, window);
                }, { passive: false });

                // Touch End -> Mouse Up (on Window)
                target.addEventListener('touchend', (e) => {
                  if (e.cancelable) e.preventDefault();
                  const touch = e.changedTouches[0];
                  dispatchMouseEvent('mouseup', touch, window);
                }, { passive: false });
            });
        };
        
        // Attempt immediate application
        applyTouch();
        
        // Also observe for changes in case content is lazy loaded/conditional
        // (Though v-color-picker structure is usually static once shown)
        // setTimeout(applyTouch, 100); 
      }
    }
  }
};
</script>
